import { useAgentStore } from '../store/agentStore.js'
import { useAuthStore } from '../store/authStore.js'
import { useCallStore } from '../store/callStore.js'
import { useChatOfferStore } from '../store/chatOfferStore.js'
import { useConversationsStore } from '../store/conversationsStore.js'
import { useLiveChatUnreadStore } from '../store/liveChatUnreadStore.js'
import { useNotificationsStore } from '../store/notificationsStore.js'
import { useApprovalCountsStore } from '../store/approvalCountsStore.js'
import { showBrowserNotification } from '../utils/browserNotifications.js'
import { useSocketStore } from '../store/socketStore.js'
import { useUiStore } from '../store/uiStore.js'
import { useReportsStore } from '../store/reportsStore.js'
import { useDialerStore } from '../store/dialerStore.js'
import { useCallbacksStore } from '../store/callbacksStore.js'
import { useVoicemailsStore } from '../store/voicemailsStore.js'
import type { Voicemail } from '../store/voicemailsStore.js'
import { api } from '../api/client.js'
import { loginPathFor } from '../auth/loginPath.js'
import type { Callback } from '../components/admin/callbacks/CallbackDrawer.js'
import { useConversations } from '../hooks/useConversations.js'
import type {
  ConversationListEntry,
  MessageStatus,
} from '../modules/conversations/types.js'
import type { GatewaySocket } from './client.js'
import type {
  ServerToClientEvents,
  AgentStateSnapshot,
  CallSessionSnapshot,
} from './types.js'
import type {
  CallIncomingEvent,
  CallAnsweredEvent,
  CallEndedEvent,
} from '@kc3/contracts'
import type { AgentStatus } from '@kc3/types'

/**
 * Wires Socket.IO server-emitted events into the Zustand stores.
 *
 * Returns a teardown function that detaches every listener registered here.
 */
export function wireSocketEvents(socket: GatewaySocket): () => void {
  const sockState = useSocketStore.getState()
  const agentState = useAgentStore.getState()
  const callState = useCallStore.getState()
  const chatOfferState = useChatOfferStore.getState()
  const uiState = useUiStore.getState()

  // §0.F reconnect refetch: when the socket drops and reconnects, the
  // conversation list may have drifted; re-pull it once on reconnect.
  const wasDisconnectedRef = { current: false }

  const onConnect = (): void => {
    sockState.setConnected(true, socket.recovered)
    if (wasDisconnectedRef.current) {
      wasDisconnectedRef.current = false
      void useConversations.getCurrent()?.refetch()
    }
  }
  const onDisconnect = (): void => {
    console.log('[Socket] DISCONNECT event fired')
    sockState.setConnected(false)
    sockState.resetQuality()
    wasDisconnectedRef.current = true
  }

  const onStatusAck: ServerToClientEvents['agent:status:ack'] = (data) => {
    console.log('[Socket] agent:status:ack received:', data)
    console.log('[Status] Setting status from agent:status:ack:', data.status)
    agentState.setStatus(data.status)
    console.log('[Status] Current agentStore status after set:', agentState.status)
  }
  const onStatusError: ServerToClientEvents['agent:status:error'] = (data) => {
    uiState.addNotification(data.message, 'error')
  }
  /**
   * Server-pushed status change — supervisor force_pause/force_logout arrive
   * here via `agent.state` (worker's seam → AMQP → gateway emit), not as a
   * reply to anything this socket sent. The store write is what repaints the
   * agent's own status pill in realtime; `forced` adds a toast so the change
   * is not confused with something they clicked.
   *
   * `data.agentId` is a users.id — compare against `user.sub`, not
   * `user.agentId` (the agents table row id). Same key the presence hash and
   * the agent room are built from.
   */
  const onAgentStateChanged: ServerToClientEvents['agent:state:changed'] = (data) => {
    if (data.agentId !== useAuthStore.getState().user?.sub) return
    agentState.setStatus(data.status)
    if (data.forced) {
      uiState.addNotification(
        `Your status was changed by a supervisor (${data.status.toLowerCase().replaceAll('_', ' ')})`,
        'warn',
      )
    }
  }
  const onAutoPaused: ServerToClientEvents['agent:auto-paused'] = () => {
    uiState.addNotification('Auto-paused: poor network quality', 'warn')
  }
  const onQualityUpdate: ServerToClientEvents['quality:update'] = (data) => {
    sockState.setQuality(data.quality, data.mos, data.packetLoss)
  }
  /**
   * Supervisor force-logout — end the session here too.
   *
   * The server side of force-logout only ever wrote presence state, so the
   * agent kept a fully working console after being "logged out" and could set
   * themselves Available again. Clearing auth is the half that makes the action
   * mean what it says.
   *
   * `window.location.href` rather than a router navigation: a hard reload is
   * what guarantees no stale in-memory state (active call, softphone
   * registration, open sockets) survives into the login screen. Read
   * imperatively from the store because this closure outlives any render.
   */
  const onForceLogout: ServerToClientEvents['agent:force_logout'] = () => {
    uiState.addNotification('You have been logged out by a supervisor', 'warn')
    useAuthStore.getState().clearSession()
    window.location.href = loginPathFor(window.location.pathname)
  }
  const onIncoming = (event: CallIncomingEvent): void => {
    // Cache only — do NOT set activeCall here. `call:incoming` is broadcast to
    // every agent in the tenant room, but only the ACD-selected agent receives
    // the SIP INVITE. Setting activeCall here made every agent's header show
    // "Client Portal Caller" even when their softphone never rang. The selected
    // agent's softphone calls `activateFromIncoming(from)` when JsSIP fires
    // `newRTCSession`, which correlates by the caller's `from` identifier and
    // sets activeCall only on the agent actually being rung.
    callState.setIncoming(event)
    uiState.addNotification(`Incoming call from ${event.from}`, 'info')
  }
  const onAnswered = (event: CallAnsweredEvent): void => {
    callState.setAnswered(event)
  }
  const onEnded = (event: CallEndedEvent): void => {
    callState.setEnded(event)
    agentState.setStatus('ACW' as AgentStatus)
  }
  const onChatOfferNew: ServerToClientEvents['chat:offer:new'] = (event) => {
    console.log('[AgentSocket] 🚨 chat:offer:new received in agent portal! Event payload:', event)
    chatOfferState.setOffer(event)
  }
  // `chat:offer:accepted` reuses the existing `widget.escalation.assigned`
  // payload shape (see Task 5's resolved design) — it is relayed to the
  // accepting agent's `agent:<userId>` room once the worker has finished
  // assigning the conversation. Storing the conversationId here (rather than
  // navigating) keeps this module router-agnostic; `ChatOfferPopup` reacts
  // to `acceptedConversationId` via its own effect.
  const onChatOfferAccepted: ServerToClientEvents['chat:offer:accepted'] = (event) => {
    chatOfferState.setAccepted(event.sessionId)
    window.dispatchEvent(
      new CustomEvent('kc3:chat:resumed', {
        detail: { chatId: event.sessionId, assignedAgentId: event.assignedAgentId },
      }),
    )
  }
  const onChatHandoffAccepted: ServerToClientEvents['chat:handoff:accepted'] = (event) => {
    chatOfferState.setAccepted(event.chatId)
  }
  const onLiveChatResumed: ServerToClientEvents['live:chat:resumed'] = (data) => {
    window.dispatchEvent(
      new CustomEvent('kc3:chat:resumed', {
        detail: { chatId: data.chatId, assignedAgentId: data.assignedAgentId },
      }),
    )
  }
  const onLiveChatEnded: ServerToClientEvents['live:chat:ended'] = (data) => {
    window.dispatchEvent(
      new CustomEvent('kc3:chat:ended', {
        detail: { chatId: data.chatId },
      }),
    )
  }
  const onStateSync = (state: AgentStateSnapshot): void => {
    console.log('[Socket] state:sync received:', state)
    console.log('[Status] Setting status from state:sync:', state.status, 'since:', state.since)
    agentState.setStatus(state.status, state.since)
    console.log('[Status] Current agentStore status after state:sync:', agentState.status)
  }
  const onReattach = (snapshot: CallSessionSnapshot): void => {
    callState.setActiveCallFromSnapshot(snapshot)
    uiState.addNotification('Reconnected to active call', 'info')
    // UI state is restored from the snapshot. The PSTN leg remains
    // bridged server-side; the SIP client will re-register and
    // receive a new re-INVITE from Asterisk automatically.
  }

  // ---- UI-1a Task 16 (§0.F-expanded): conversation socket events ----
  // Observability (§0.L): info-level log on each handler (event name +
  // conversationId + tenantSlug when present). Matches the lightweight
  // logging style available client-side (no request.log in the browser).
  const logConv = (
    event: string,
    conversationId: string | undefined,
    tenantSlug?: string,
  ): void => {
    // eslint-disable-next-line no-console -- socket diagnostic logging is intentionally printed to the browser console for real-time debugging; not a product log
    console.info(
      `[socket] ${event} conversationId=${conversationId ?? '?'}` +
        (tenantSlug ? ` tenantSlug=${tenantSlug}` : ''),
    )
  }

  // `conversation:opened` — UCP-0c enriched payload. Read enriched fields
  // (primaryHandle, displayName, lastMessageAt, slaState) directly; do not
  // synthesize defaults for them.
  const onConversationOpened = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      referenceId?: string
      channelOrigin?: ConversationListEntry['channelOrigin']
      primaryHandle?: string
      displayName?: string
      lastMessagePreview?: string
      lastMessageAt?: string
      status?: 'open' | 'closed'
      slaState?: ConversationListEntry['slaState']
      assignedAgentId?: string | null
      assignedQueueId?: string | null
    }
    if (!e.conversationId) return
    logConv('conversation:opened', e.conversationId, e.tenantSlug)
    const entry: ConversationListEntry = {
      id: e.conversationId,
      channelOrigin: e.channelOrigin ?? 'whatsapp',
      referenceId: e.referenceId ?? '',
      primaryHandle: e.primaryHandle ?? '',
      displayName: e.displayName ?? '',
      lastMessagePreview: e.lastMessagePreview ?? '',
      lastMessageAt: e.lastMessageAt ?? new Date().toISOString(),
      status: e.status ?? 'open',
      // The socket payload carries no catalog status; the row picks it up on
      // the next list fetch, and until then it falls back to the lifecycle value.
      conversationStatusId: null,
      statusLabel: null,
      statusColor: null,
      statusColorType: null,
      slaState: e.slaState ?? 'none',
      assignedAgentId: e.assignedAgentId ?? null,
      assignedQueueId: e.assignedQueueId ?? null,
      priorityId: null,
      urgencyId: null,
      impactId: null,
      categoryId: null,
      subcategoryId: null,
      typeId: null,
      transferCount: 0,
      csatScore: null,
      approvalStatus: null,
      subject: null,
      description: null,
      firstResponseDueAt: null,
      resolutionDueAt: null,
      olaDueAt: null,
      slaBreached: false,
      olaBreached: false,
      firstRespondedAt: null,
      resolvedAt: null,
      openedAt: e.lastMessageAt ?? new Date().toISOString(),
      slaStartedAt: null,
      slaPolicyId: null,
      olaPolicyId: null,
      slaPolicyName: null,
      olaPolicyName: null,
      parentId: null,
      isParent: false,
      parentReferenceId: null,
      // The socket payload carries none of the relation/approval detail; the
      // row picks it up on the next list fetch. Defaults keep a freshly-opened
      // conversation looking unlinked rather than guessing.
      approvalStateId: null,
      linkedRelationCount: 0,
      childCount: 0,
      childReferenceIds: [],
      mergedIntoId: null,
      mergedIntoReferenceId: null,
      secondaryCount: 0,
      secondaryReferenceIds: [],
    }
    useConversationsStore.getState().prependItem(entry)
  }

  // `conversation:message:new` — UCP-0c enriched payload carries
  // lastMessagePreview + lastMessageAt; read them directly.
  const onConversationMessageNew = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      lastMessagePreview?: string
      lastMessageAt?: string
    }
    if (!e.conversationId) return
    const store = useConversationsStore.getState()
    if (!store.items.some((i) => i.id === e.conversationId)) return
    logConv('conversation:message:new', e.conversationId, e.tenantSlug)
    store.patchListItem(e.conversationId, {
      lastMessagePreview: e.lastMessagePreview ?? '',
      lastMessageAt: e.lastMessageAt ?? new Date().toISOString(),
    })
    store.reorderToTop(e.conversationId)
  }

  // UI-1b Task 7: patch the message status in messagesByConversationId by id
  // when a delivery receipt arrives. Real contract payload (channel.ts
  // ConversationMessageStatusChangedEventSchema): { conversationId, messageId,
  // status: 'sent'|'delivered'|'read'|'failed'|'bounced' }. `bounced` maps to
  // the UI's terminal failure state.
  const onConversationMessageStatusChanged = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      messageId?: string
      status?: string
      providerErrorCode?: string
      tenantSlug?: string
    }
    logConv('conversation:message:status:changed', e.conversationId, e.tenantSlug)
    if (!e.conversationId || !e.messageId || !e.status) return
    const store = useConversationsStore.getState()
    const list = store.messagesByConversationId[e.conversationId]
    if (!list?.some((m) => m.id === e.messageId)) return
    const status: MessageStatus = e.status === 'bounced' ? 'failed' : (e.status as MessageStatus)
    store.patchMessage(e.conversationId, e.messageId, { status })
    // Surface why a receipt-driven failure happened so the failed bubble can
    // show the provider error (resolves UI-1b TODO).
    if (status === 'failed' && e.providerErrorCode) {
      store.setFailureReason(
        e.messageId,
        `Delivery failed (${e.providerErrorCode})`,
      )
    }
  }

  // UI-1e Task 14: message-approval requested. Upsert the approval into the
  // store and, if the drafted message is already in store, flip its status to
  // 'pending_approval' (cross-conversation safe — only patch if present).
  const onMessageApprovalRequested = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      messageId?: string
      approvalId?: string
      requestedByAgentId?: string
      approverAgentId?: string | null
      createdAt?: string
      tenantSlug?: string
    }
    if (!e.conversationId || !e.messageId || !e.approvalId) return
    logConv('conversation:message_approval:requested', e.conversationId, e.tenantSlug)
    const store = useConversationsStore.getState()
    store.upsertMessageApproval({
      id: e.approvalId,
      messageId: e.messageId,
      conversationId: e.conversationId,
      status: 'pending',
      requestedByAgentId: e.requestedByAgentId ?? '',
      approverAgentId: e.approverAgentId ?? null,
      decidedByAgentId: null,
      rejectionReason: null,
      createdAt: e.createdAt ?? new Date().toISOString(),
    })
    const list = store.messagesByConversationId[e.conversationId]
    if (list?.some((m) => m.id === e.messageId)) {
      store.patchMessage(e.conversationId, e.messageId, {
        status: 'pending_approval',
      })
    }

    // Live-update the header approval-count badge (ApprovalDropdownWidget)
    // — both fields are already resolved to real agents.id server-side.
    const myAgentId = useAuthStore.getState().user?.agentId
    if (myAgentId) {
      if (e.approverAgentId === myAgentId) {
        useApprovalCountsStore.getState().bumpPendingForApprover()
      }
      if (e.requestedByAgentId === myAgentId) {
        useApprovalCountsStore.getState().bumpPendingForRequester()
      }
    }
  }

  // UI-1e Task 14: message-approval decided. Update the approval status and
  // patch the message status: approved → 'sent' (backend actually sent it),
  // rejected → 'failed' + setFailureReason, cancelled → 'cancelled'.
  const onMessageApprovalDecided = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      messageId?: string
      approvalId?: string
      decision?: 'approved' | 'rejected' | 'cancelled'
      decidedByAgentId?: string | null
      rejectionReason?: string
      tenantSlug?: string
    }
    if (!e.conversationId || !e.messageId || !e.approvalId || !e.decision) return
    logConv('conversation:message_approval:decided', e.conversationId, e.tenantSlug)
    const store = useConversationsStore.getState()
    const existing = store.messageApprovalsByMessageId[e.messageId]
    store.upsertMessageApproval({
      id: e.approvalId,
      messageId: e.messageId,
      conversationId: e.conversationId,
      status: e.decision,
      requestedByAgentId: existing?.requestedByAgentId ?? '',
      approverAgentId: existing?.approverAgentId ?? null,
      decidedByAgentId: e.decidedByAgentId ?? null,
      rejectionReason: e.rejectionReason ?? null,
      createdAt: existing?.createdAt ?? new Date().toISOString(),
    })

    // Live-update the header approval-count badge. Only "approved"/"rejected"
    // move a pending count — "cancelled" (self-cancel by the requester) never
    // incremented the approver-side pending count in the first place, and
    // requester-side cancellation isn't reflected in this badge today.
    if (e.decision === 'approved' || e.decision === 'rejected') {
      const myAgentId = useAuthStore.getState().user?.agentId
      if (myAgentId) {
        if (existing?.approverAgentId === myAgentId) {
          useApprovalCountsStore.getState().resolvePending('approver', e.decision)
        }
        if (existing?.requestedByAgentId === myAgentId) {
          useApprovalCountsStore.getState().resolvePending('requester', e.decision)
        }
      }
    }

    const list = store.messagesByConversationId[e.conversationId]
    if (!list?.some((m) => m.id === e.messageId)) return
    if (e.decision === 'approved') {
      store.patchMessage(e.conversationId, e.messageId, { status: 'sent' })
    } else if (e.decision === 'rejected') {
      store.patchMessage(e.conversationId, e.messageId, { status: 'failed' })
      if (e.rejectionReason) {
        store.setFailureReason(e.messageId, e.rejectionReason)
      }
    } else {
      store.patchMessage(e.conversationId, e.messageId, { status: 'cancelled' })
    }
  }

  // UI-1f Task 9: co-agent added. Append the agentId (dedupe) to the
  // in-store conversation's coAgentIds so CoAgentDialog re-renders.
  // Cross-conversation safe — addCoAgent no-ops if the conversation is
  // not present in the store.
  const onCoAgentAdded = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      agentId?: string
      addedByAgentId?: string | null
      tenantSlug?: string
    }
    if (!e.conversationId || !e.agentId) return
    logConv('conversation:co_agent:added', e.conversationId, e.tenantSlug)
    useConversationsStore.getState().addCoAgent(e.conversationId, e.agentId)
  }

  // UI-1f Task 9: co-agent removed. Strip the agentId from the in-store
  // conversation's coAgentIds (no-op if conversation absent).
  const onCoAgentRemoved = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      agentId?: string
      removedByAgentId?: string | null
      tenantSlug?: string
    }
    if (!e.conversationId || !e.agentId) return
    logConv('conversation:co_agent:removed', e.conversationId, e.tenantSlug)
    useConversationsStore.getState().removeCoAgent(e.conversationId, e.agentId)
  }

  // `conversation:transferred` — UCP-0c uses to/from Agent/Queue ids.
  const onConversationTransferred = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      toAgentId?: string | null
      toQueueId?: string | null
    }
    if (!e.conversationId) return
    const store = useConversationsStore.getState()
    const currentAgentId = useAuthStore.getState().user?.sub ?? null
    const myQueueIds = useAuthStore.getState().user?.queueMemberships ?? []
    const isReceived =
      (e.toAgentId != null && e.toAgentId === currentAgentId) ||
      (e.toQueueId != null && myQueueIds.includes(e.toQueueId))

    if (!store.items.some((i) => i.id === e.conversationId)) {
      // Conversation not in this agent's list — if it was transferred TO
      // this agent, the ws-gateway also emits conversation:list:bump which
      // triggers useInboxConversations to refetch the list + counts.
      if (isReceived) {
        logConv('conversation:transferred (received, waiting for list refetch)', e.conversationId, e.tenantSlug)
      }
      return
    }
    logConv('conversation:transferred', e.conversationId, e.tenantSlug)
    // Determine scope: is this conversation still in this agent's view?
    const stillInScope =
      (e.toAgentId != null && e.toAgentId === currentAgentId) ||
      (e.toQueueId != null && myQueueIds.includes(e.toQueueId))
    if (!stillInScope && store.filters.assignee === 'me-and-queues') {
      store.removeListItem(e.conversationId)
      // If this was the selected conversation, raise the banner signal.
      if (store.selectedId === e.conversationId) {
        store.setSelectedTransferredOut(true)
      }
    } else {
      store.patchListItem(e.conversationId, {
        assignedAgentId: e.toAgentId ?? null,
        assignedQueueId: e.toQueueId ?? null,
      })
    }
  }

  // `conversation:closed` — §0.F line 200 sanctions the simpler version:
  // patch/remove from list and rely on detail status to drive the banner
  // (Task 12). No synthetic-activity append / getCurrentForId.
  const onConversationClosed = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      closedAt?: string
      resolutionType?: string
    }
    if (!e.conversationId) return
    const store = useConversationsStore.getState()
    if (!store.items.some((i) => i.id === e.conversationId)) return
    logConv('conversation:closed', e.conversationId, e.tenantSlug)
    if (store.filters.status === 'open') {
      store.removeListItem(e.conversationId)
    } else {
      store.patchListItem(e.conversationId, { status: 'closed' })
    }
  }

  // Real-time priority changes
  const onConversationPriorityChanged = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      priorityId?: string | null
      urgencyId?: string | null
      impactId?: string | null
      complexityId?: string | null
    }
    if (!e.conversationId) return
    const store = useConversationsStore.getState()
    if (!store.items.some((i) => i.id === e.conversationId)) return
    logConv('conversation:priority:changed', e.conversationId, e.tenantSlug)
    store.patchListItem(e.conversationId, {
      priorityId: e.priorityId ?? null,
      urgencyId: e.urgencyId ?? null,
      impactId: e.impactId ?? null,
    })
  }

  // Real-time status changes
  const onConversationStatusChanged = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      statusId?: string | null
      status?: 'open' | 'closed'
    }
    if (!e.conversationId) return
    const store = useConversationsStore.getState()
    if (!store.items.some((i) => i.id === e.conversationId)) return
    logConv('conversation:status:changed', e.conversationId, e.tenantSlug)
    if (e.status) {
      if (store.filters.status === 'open' && e.status === 'closed') {
        store.removeListItem(e.conversationId)
      } else if (store.filters.status === 'closed' && e.status === 'open') {
        store.removeListItem(e.conversationId)
      } else {
        store.patchListItem(e.conversationId, { status: e.status })
      }
    }
  }

  // Real-time classification changes
  const onConversationClassificationChanged = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      categoryId?: string | null
      subcategoryId?: string | null
      typeId?: string | null
      subtypeId?: string | null
    }
    if (!e.conversationId) return
    const store = useConversationsStore.getState()
    if (!store.items.some((i) => i.id === e.conversationId)) return
    logConv('conversation:classification:changed', e.conversationId, e.tenantSlug)
    store.patchListItem(e.conversationId, {
      categoryId: e.categoryId ?? null,
      subcategoryId: e.subcategoryId ?? null,
      typeId: e.typeId ?? null,
    })
  }

  // Real-time CSAT updates
  const onConversationCsatUpdated = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      csatScore?: number | null
      csatSubmittedAt?: string | null
    }
    if (!e.conversationId) return
    const store = useConversationsStore.getState()
    if (!store.items.some((i) => i.id === e.conversationId)) return
    logConv('conversation:csat:updated', e.conversationId, e.tenantSlug)
    store.patchListItem(e.conversationId, {
      csatScore: e.csatScore ?? null,
    })
  }

  // Real-time resolution
  const onConversationResolved = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      resolvedAt?: string
      resolvedByAgentId?: string
    }
    if (!e.conversationId) return
    const store = useConversationsStore.getState()
    if (!store.items.some((i) => i.id === e.conversationId)) return
    logConv('conversation:resolved', e.conversationId, e.tenantSlug)
    // Resolution typically means closed status
    if (store.filters.status === 'open') {
      store.removeListItem(e.conversationId)
    } else {
      store.patchListItem(e.conversationId, { status: 'closed' })
    }
  }

  // Real-time assignment changes
  const onConversationAssigned = (data: unknown): void => {
    const e = data as {
      conversationId?: string
      tenantSlug?: string
      assignedAgentId?: string | null
      assignedQueueId?: string | null
      assignedTeamId?: string | null
      previousAgentId?: string | null
    }
    if (!e.conversationId) return
    const store = useConversationsStore.getState()
    if (!store.items.some((i) => i.id === e.conversationId)) return
    logConv('conversation:assigned', e.conversationId, e.tenantSlug)
    
    // Check if conversation should remain in current filter view
    const currentAgentId = useAuthStore.getState().user?.sub ?? null
    const myQueueIds = useAuthStore.getState().user?.queueMemberships ?? []
    const stillInScope =
      store.filters.assignee === 'all' ||
      (store.filters.assignee === 'me-only' && e.assignedAgentId === currentAgentId) ||
      (store.filters.assignee === 'me-and-queues' && 
        (e.assignedAgentId === currentAgentId || 
         (e.assignedQueueId != null && myQueueIds.includes(e.assignedQueueId)))) ||
      (store.filters.assignee === 'unassigned' && e.assignedAgentId === null)
    
    if (!stillInScope) {
      store.removeListItem(e.conversationId)
      if (store.selectedId === e.conversationId) {
        store.setSelectedTransferredOut(true)
      }
    } else {
      store.patchListItem(e.conversationId, {
        assignedAgentId: e.assignedAgentId ?? null,
        assignedQueueId: e.assignedQueueId ?? null,
      })
    }
  }

  // In-app notification center: a live `notification:new` push writes into the
  // notifications store (the only cross-component signal — the bell widget
  // subscribes to it) and fires a native desktop notification alongside.
  const onNotificationNew = (data: unknown): void => {
    console.log('[Socket] Received notification:new event:', data)
    const e = data as {
      notificationId?: string
      eventKey?: string
      title?: string
      description?: string
      conversationId?: string | null
      campaignName?: string | null
      actorName?: string | null
      referenceId?: string | null
      subject?: string | null
      assigneeName?: string | null
      recipientName?: string | null
      entityType?: string | null
    }
    if (!e.notificationId) {
      console.warn('[Socket] notification:new event missing notificationId')
      return
    }
    console.log('[Socket] Adding live notification to store:', e)
    useNotificationsStore.getState().addLive({
      id: e.notificationId,
      eventKey: e.eventKey ?? '',
      title: e.title ?? '',
      description: e.description ?? '',
      conversationId: e.conversationId ?? null,
      campaignName: e.campaignName ?? null,
      isRead: false,
      createdAt: new Date().toISOString(),
      actorName: e.actorName ?? null,
      referenceId: e.referenceId ?? null,
      subject: e.subject ?? null,
      assigneeName: e.assigneeName ?? null,
      recipientName: e.recipientName ?? null,
      entityType: e.entityType ?? null,
    })
    showBrowserNotification(e.title ?? 'New notification', e.description ?? '')

    // Live Chat sidebar: a client_replied notification on a still-staged
    // Live Chat (entityType 'live_chat' — distinct from the same eventKey
    // firing on a finalized conversation, e.g. from the ticket ingest path)
    // that the agent isn't currently viewing marks that row unread. The
    // toast above (via notificationsStore.toastNotification →
    // NotificationToast) already covers "let the agent know" for every type.
    if (e.entityType === 'live_chat' && e.conversationId) {
      const liveChatStore = useLiveChatUnreadStore.getState()
      if (liveChatStore.selectedId !== e.conversationId) {
        liveChatStore.markUnread(e.conversationId)
      }
    }
  }
  socket.on('notification:new', onNotificationNew)

  socket.on('connect', onConnect)
  socket.on('disconnect', onDisconnect)
  socket.on('conversation:opened', onConversationOpened)
  socket.on('conversation:message:new', onConversationMessageNew)
  socket.on(
    'conversation:message:status:changed',
    onConversationMessageStatusChanged,
  )
  socket.on('conversation:message_approval:requested', onMessageApprovalRequested)
  socket.on('conversation:message_approval:decided', onMessageApprovalDecided)
  socket.on('conversation:co_agent:added', onCoAgentAdded)
  socket.on('conversation:co_agent:removed', onCoAgentRemoved)
  socket.on('conversation:transferred', onConversationTransferred)
  socket.on('conversation:closed', onConversationClosed)
  socket.on('conversation:priority:changed', onConversationPriorityChanged)
  socket.on('conversation:status:changed', onConversationStatusChanged)
  socket.on('conversation:classification:changed', onConversationClassificationChanged)
  socket.on('conversation:csat:updated', onConversationCsatUpdated)
  socket.on('conversation:resolved', onConversationResolved)
  socket.on('conversation:assigned', onConversationAssigned)
  socket.on('agent:status:ack', onStatusAck)
  socket.on('agent:status:error', onStatusError)
  socket.on('agent:state:changed', onAgentStateChanged)
  socket.on('agent:auto-paused', onAutoPaused)
  socket.on('quality:update', onQualityUpdate)
  socket.on('agent:force_logout', onForceLogout)
  socket.on('call:incoming', onIncoming)
  socket.on('call:answered', onAnswered)
  socket.on('call:ended', onEnded)
  socket.on('chat:offer:new', onChatOfferNew)
  socket.on('chat:offer:accepted', onChatOfferAccepted)
  socket.on('chat:handoff:accepted', onChatHandoffAccepted)
  socket.on('live:chat:resumed', onLiveChatResumed)
  socket.on('live:chat:ended', onLiveChatEnded)
  socket.on('state:sync', onStateSync)
  socket.on('call:session:reattach', onReattach)

  const onDashboardUpdate: ServerToClientEvents['dashboard:update'] = (data) => {
    useReportsStore.getState().setRealtimeData(data)
  }
  socket.on('dashboard:update', onDashboardUpdate)

  const onDialerPacingUpdate: ServerToClientEvents['dialer:pacing_update'] = (data) => {
    useDialerStore.getState().setPacing({
      dialRate: data.dialRate,
      abandonRate: data.abandonRate,
      pacingFactor: data.pacingFactor,
    })
  }
  socket.on('dialer:pacing_update', onDialerPacingUpdate)

  const onDialerCampaignUpdate: ServerToClientEvents['dialer:campaign_update'] = (data) => {
    useDialerStore.getState().setCampaigns(
      useDialerStore.getState().campaigns.map((c) => (c.id === data.id ? { ...c, ...data } : c)),
    )
  }
  socket.on('dialer:campaign_update', onDialerCampaignUpdate)

  const onDialerPreviewContact: ServerToClientEvents['dialer:preview_contact'] = (data) => {
    useDialerStore.getState().setPreviewContact(data)
  }
  socket.on('dialer:preview_contact', onDialerPreviewContact)

  // ---- UI-3c Task 7: scheduled-callback lifecycle events ----
  // The callbacksStore.myCallbacks list is already filtered to MY pending
  // callbacks (useMyCallbacks fetches with agentId=me), so for the
  // agent-less `callback:originated` / `callback:failed` events a
  // local-list membership check ("is this callbackId in my list?")
  // implicitly answers "was this callback mine?" — see §0.D.

  // `callback:scheduled` carries `agentId` — prepend ONLY when it matches the
  // current agent so other agents' scheduled callbacks don't pollute the
  // widget. `prependCallback` is idempotent (a poll may have fetched it).
  const onCallbackScheduled: ServerToClientEvents['callback:scheduled'] = (data) => {
    const currentAgentId = useAuthStore.getState().user?.sub ?? null
    if (!currentAgentId || data.agentId !== currentAgentId) return
    const callback: Callback = {
      id: data.callbackId,
      callId: null,
      phoneE164: data.phoneE164,
      agentId: data.agentId,
      status: 'PENDING',
      scheduledAt: data.scheduledAt,
      maxAttempts: 3,
      attemptCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    useCallbacksStore.getState().prependCallback(callback)
  }
  socket.on('callback:scheduled', onCallbackScheduled)

  // `callback:originated` carries no `agentId`; remove only if the callbackId
  // is in the local list (membership ⇒ it was mine). Otherwise ignore.
  const onCallbackOriginated: ServerToClientEvents['callback:originated'] = (data) => {
    const store = useCallbacksStore.getState()
    if (store.myCallbacks.some((c) => c.id === data.callbackId)) {
      store.removeCallback(data.callbackId)
    }
  }
  socket.on('callback:originated', onCallbackOriginated)

  // `callback:failed` carries `{ callbackId, reason }`; patch the matching
  // callback (status → FAILED) only if it's in the local list.
  const onCallbackFailed: ServerToClientEvents['callback:failed'] = (data) => {
    const store = useCallbacksStore.getState()
    if (store.myCallbacks.some((c) => c.id === data.callbackId)) {
      store.patchCallback(data.callbackId, { status: 'FAILED' })
    }
  }
  socket.on('callback:failed', onCallbackFailed)

  // ---- UI-5b Task 13: voicemail:new — refetch approach ----
  // The event payload lacks `recordingId`/`transcript` (they are
  // only on the persisted row). Rather than constructing a partial voicemail
  // that cannot play audio, we trigger a full refetch so the store reflects
  // complete rows. Events for other agents (or IVR nulls) are ignored via
  // the agentId guard.
  const onVoicemailNew: ServerToClientEvents['voicemail:new'] = (data) => {
    const currentAgentId = useAuthStore.getState().user?.sub ?? null
    if (!currentAgentId || data.agentId !== currentAgentId) return
    void api
      .get<{ voicemails: Voicemail[] }>('/voicemails?status=NEW&limit=50')
      .then((resp) => {
        useVoicemailsStore.getState().setMyVoicemails(resp.voicemails)
      })
      .catch(() => {
        // Leave store as-is on error; the badge's polling will recover.
      })
  }
  socket.on('voicemail:new', onVoicemailNew)

  return () => {
    socket.off('notification:new', onNotificationNew)
    socket.off('connect', onConnect)
    socket.off('disconnect', onDisconnect)
    socket.off('conversation:opened', onConversationOpened)
    socket.off('conversation:message:new', onConversationMessageNew)
    socket.off(
      'conversation:message:status:changed',
      onConversationMessageStatusChanged,
    )
    socket.off('conversation:message_approval:requested', onMessageApprovalRequested)
    socket.off('conversation:message_approval:decided', onMessageApprovalDecided)
    socket.off('conversation:co_agent:added', onCoAgentAdded)
    socket.off('conversation:co_agent:removed', onCoAgentRemoved)
    socket.off('conversation:transferred', onConversationTransferred)
    socket.off('conversation:closed', onConversationClosed)
    socket.off('conversation:priority:changed', onConversationPriorityChanged)
    socket.off('conversation:status:changed', onConversationStatusChanged)
    socket.off('conversation:classification:changed', onConversationClassificationChanged)
    socket.off('conversation:csat:updated', onConversationCsatUpdated)
    socket.off('conversation:resolved', onConversationResolved)
    socket.off('conversation:assigned', onConversationAssigned)
    socket.off('agent:status:ack', onStatusAck)
    socket.off('agent:status:error', onStatusError)
    socket.off('agent:state:changed', onAgentStateChanged)
    socket.off('agent:auto-paused', onAutoPaused)
    socket.off('agent:force_logout', onForceLogout)
    socket.off('quality:update', onQualityUpdate)
    socket.off('call:incoming', onIncoming)
    socket.off('call:answered', onAnswered)
    socket.off('call:ended', onEnded)
    socket.off('chat:offer:new', onChatOfferNew)
    socket.off('chat:offer:accepted', onChatOfferAccepted)
    socket.off('chat:handoff:accepted', onChatHandoffAccepted)
    socket.off('live:chat:resumed', onLiveChatResumed)
    socket.off('live:chat:ended', onLiveChatEnded)
    socket.off('state:sync', onStateSync)
    socket.off('call:session:reattach', onReattach)
    socket.off('dashboard:update', onDashboardUpdate)
    socket.off('dialer:pacing_update', onDialerPacingUpdate)
    socket.off('dialer:campaign_update', onDialerCampaignUpdate)
    socket.off('dialer:preview_contact', onDialerPreviewContact)
    socket.off('callback:scheduled', onCallbackScheduled)
    socket.off('callback:originated', onCallbackOriginated)
    socket.off('callback:failed', onCallbackFailed)
    socket.off('voicemail:new', onVoicemailNew)
  }
}

/**
 * Fresh-page-load probe: ask the gateway for any persisted call
 * session for this agent and, if present, restore UI state. Uses the
 * Socket.IO ack callback so the response is correlated.
 */
export function resumeSession(socket: GatewaySocket): void {
  socket.emit('call:session:resume', (snap) => {
    if (snap) {
      useCallStore.getState().setActiveCallFromSnapshot(snap)
    }
  })
}
